<script setup>
import {useLocalePath} from 'vue-i18n-routing'

const localePath = useLocalePath()
const {locale} = useI18n()

const props = defineProps({
  content: {
    type: Object,
    required: true,
  },
})

const estimateReadingTime = ref(0)

const calculateReadingTime = () => {
  const wordsPerMinute = 200
  let totalWords = 0

  props.content.section.paragraphs.forEach((paragraph) => {
    if (paragraph.text) {
      totalWords += paragraph.text.split(' ').length
    }
  })

  estimateReadingTime.value = Math.ceil(totalWords / wordsPerMinute)
}

onMounted(() => {
  calculateReadingTime()
})

const titleTextParagraph = resolveComponent('SharedParagraphsTitleText')
const screamerTextParagraph = resolveComponent('SharedParagraphsScreamerText')
const galleryParagraph = resolveComponent('SharedParagraphsSlider')
const embeddedVideoParagraph = resolveComponent('SharedParagraphsEmbeddedVideo')
const downloadsParagraph = resolveComponent('SharedParagraphsDownloads')

const getParagraphComponent = (type) => {
  switch (type) {
    case 'title_text':
      return titleTextParagraph
    case 'screamer_text':
      return screamerTextParagraph
    case 'gallery':
      return galleryParagraph
    case 'embedded_video':
      return embeddedVideoParagraph
    case 'downloads':
      return downloadsParagraph
    default:
      return ''
  }
}
</script>

<template>
  <section class="news-content contains-iframe">
    <div class="news-content-wrapper">
      <div class="news-content-left">
        <ul class="news-meta-list animate-in-stagger-left animate-out-left">
          <li class="news-meta-item animate-stagger-item">
            <div class="news-meta-label">{{ $t('category') }}</div>
            <div class="news-meta-info">{{ content.section.category }}</div>
          </li>
          <li class="news-meta-item animate-stagger-item">
            <div class="news-meta-label">
              {{ $t('reading_time') }}
            </div>
            <div class="news-meta-info">
              {{ estimateReadingTime }} {{ $t('minutes') }}
            </div>
          </li>
          <li class="news-meta-item animate-stagger-item">
            <div class="news-meta-label">{{ $t('date') }}</div>
            <div class="news-meta-info">
              {{ useDateFormatter(content.section.date, locale) }}
            </div>
          </li>
        </ul>
        <SharedShareMenu
          class="news-share-list animate-in-stagger-left animate-out-left" />
        <div class="news-related animate-in-stagger-left animate-out-left">
          <SharedCardsRelatedContent
            v-if="content.section.related.project"
            :type="'projects'"
            :classes="'animate-stagger-item'"
            :slug="content.section.related.project.slug"
            :label="$t('project')"
            :title="content.section.related.project.title"
            :images="content.section.related.project.assets"/>
          <SharedCardsRelatedContent
            v-if="content.section.related.service"
            :type="'services'"
            :classes="'animate-stagger-item'"
            :slug="content.section.related.service.slug"
            :label="$t('service')"
            :title="content.section.related.service.title"/>
        </div>
      </div>
      <div class="news-content-right animate-in-bottom animate-out-right">
        <div
          v-for="(paragraph, index) in content.section.paragraphs"
          :key="index">
          <component
            :is="getParagraphComponent(paragraph.type)"
            :content="paragraph" />
        </div>
      </div>
      <div class="news-related mobile animate-in-stagger-left animate-out-left">
        <SharedCardsRelatedContent
          v-if="content.section.related.project"
          :classes="'animate-stagger-item'"
          :slug="content.section.related.project.slug"
          :label="$t('project')"
          :title="content.section.related.project.title"
          :images="content.section.related.project.assets"/>
        <SharedCardsRelatedContent
          v-if="content.section.related.service"
          :classes="'animate-stagger-item'"
          :slug="content.section.related.service.slug"
          :label="$t('service')"
          :title="content.section.related.service.title"/>
      </div>
    </div>
  </section>
</template>

<style lang="scss">
.news-content {
  .news-content-wrapper {
    @include make-col-ready();
    margin-bottom: pxrem(100);
    padding-top: pxrem(30);

    @include mq(md) {
      @include make-col(22);
      @include make-col-offset(1);
      display: flex;
      justify-content: space-between;
      margin-bottom: pxrem(140);
      padding-top: pxrem(70);
    }
  }

  .news-content-left {
    @include make-col-ready;
    display: flex;
    flex-direction: column-reverse;

    @include mq(md) {
      @include make-col(5);
      display: block;
    }

    @include mq(lg) {
      @include make-col(4);
    }
  }

  .news-related{
    display: none;

    @include mq(lg) {
      display: block;
    }

    &.mobile{
      display: block;

      @include mq(lg) {
        display: none;
      }
    }
  }

  .news-meta-list {
    @include list-reset;
  }

  .news-meta-item {
    border-bottom: 1px solid $color-gray;
    margin-bottom: pxrem(20);

    &:last-of-type {
      border-bottom: none;
    }
  }

  .news-meta-label {
    @include label($color-gray-dark);
    margin-bottom: pxrem(3);
  }

  .news-meta-info {
    @include label;
    padding-bottom: pxrem(20);
  }

  .news-content-right {
    @include make-col-ready;

    @include mq(md) {
      @include make-col-offset(3);
    }

    @include mq(lg) {
      @include make-col(14);
      @include make-col-offset(4);
    }
  }

  .paragraph {
    border-bottom: 1px solid $color-gray;

    &:last-of-type {
      border-bottom: none;
    }
  }

  .section--download {
    padding-bottom: pxrem(10);
    padding-top: pxrem(10);

    @include mq(md) {
      padding-bottom: pxrem(30);
      padding-top: pxrem(30);
    }

    .section-wrapper {
      @include make-col-ready;

      @include mq(md) {
        @include make-col-offset(1);
        margin-left: 0;
      }
    }
  }
}
</style>
